import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { SingleValue, MultiValue, OptionProps, GroupBase, MenuListProps, components as selectComponents } from "react-select";
import CustomToolTip from "../../../CommonComponents/CustomToolTip";
import CustomSelect from "../../../CommonComponents/CustomSelect";
import {
  InfoIcon,
  CheckMiniIcon,
  TelemetryAllFilterIcon,
  TelemetryRelevantFilterIcon,
  TelemetryUnassociatedFilterIcon,
} from "./svgs/TestCaseSvgs";
import { TelemetryOption, TelemetryValueContainer, SelectOption } from "./TestCaseSelectStyles";

const SELECT_ALL_VALUE = "__select_all__";

type TelemetryFilter = "priority" | "unassociated" | "all";
type TelemetryOptionGroup = "recommended" | "secondary" | "unassociated" | "relevant";

interface TelemetrySearchSummary {
  total: number;
  relevant: number;
  unassociated: number;
}

const TELEMETRY_FILTERS: { key: TelemetryFilter; label: string; Icon: () => JSX.Element }[] = [
  { key: "priority", label: "Relevant", Icon: TelemetryRelevantFilterIcon },
  { key: "unassociated", label: "Unassociated", Icon: TelemetryUnassociatedFilterIcon },
  { key: "all", label: "All", Icon: TelemetryAllFilterIcon },
];

const formatTelemetryFilterCount = (count: number): string => {
  if (count < 1000) return String(count);
  const compact = count / 1000;
  return `${compact >= 10 ? Math.round(compact) : compact.toFixed(1).replace(".0", "")}k`;
};

const getTelemetrySearchTerms = (searchText: string): string[] => searchText.trim().split(/\s+/).filter(Boolean);

const TelemetryFilterLabel = ({ label, active }: { label: string; active: boolean }): JSX.Element => {
  const labelRef = useRef<HTMLSpanElement | null>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  const updateTruncationState = useCallback(() => {
    const labelEl = labelRef.current;
    if (!labelEl) return;
    setIsTruncated(labelEl.scrollWidth > labelEl.clientWidth + 1);
  }, []);

  useEffect(() => {
    const frame = window.requestAnimationFrame(updateTruncationState);
    window.addEventListener("resize", updateTruncationState);

    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("resize", updateTruncationState);
    };
  }, [active, label, updateTruncationState]);

  return (
    <CustomToolTip
      title={label}
      placement="top"
      disabled={!isTruncated}
      maxWidth="180px"
      wordBreak="normal"
      className="tcd-tm-option-filter__label-wrap"
    >
      <span
        ref={labelRef}
        className="tcd-tm-option-filter__label"
        onMouseEnter={updateTruncationState}
        onFocus={updateTruncationState}
        onTransitionEnd={updateTruncationState}
      >
        {label}
      </span>
    </CustomToolTip>
  );
};

const getTelemetryOptionGroup = (option: SelectOption): TelemetryOptionGroup => {
  if (option.isRecommended) return "recommended";
  if (option.isSecondary) return "secondary";
  if (option.isUnassociated) return "unassociated";
  return "relevant";
};

const getTelemetryOptionSectionLabel = (option: SelectOption): string => {
  if (option.isRecommended) return "Recommended TMs";
  if (option.isSecondary) return "Secondary TMs";
  if (option.isUnassociated) return "Unassociated TMs";
  return "Relevant TMs";
};

interface LeftPanelProps {
  busComponents: SelectOption[];
  component: SelectOption | null;
  selectedCommand: SelectOption | null;
  selectedTelemetries: SelectOption[];
  telemetryOptions: SelectOption[];
  delay: string;
  timeout: string;
  isCritical: boolean;
  isComponentsLoading?: boolean;
  isTelemetriesLoading?: boolean;
  forceCloseTelemetryMenu?: boolean;
  isReadOnly?: boolean;
  onComponentChange: (option: SingleValue<SelectOption>) => void;
  onTelecommandChange: (option: SingleValue<SelectOption>) => void;
  onTelemetriesChange: (vals: MultiValue<SelectOption>) => void;
  onDelayChange: (val: string) => void;
  onTimeoutChange: (val: string) => void;
  onIsCriticalChange: (val: boolean) => void;
}

const LeftPanel = ({
  busComponents,
  component,
  selectedCommand,
  selectedTelemetries,
  telemetryOptions,
  delay,
  timeout,
  isCritical,
  isComponentsLoading = false,
  isTelemetriesLoading = false,
  forceCloseTelemetryMenu = false,
  isReadOnly = false,
  onComponentChange,
  onTelecommandChange,
  onTelemetriesChange,
  onDelayChange,
  onTimeoutChange,
  onIsCriticalChange,
}: LeftPanelProps): JSX.Element => {
  const timeoutInputWrapRef = useRef<HTMLDivElement | null>(null);
  const [timeoutBubblePos, setTimeoutBubblePos] = useState<{ top: number; left: number } | null>(null);
  const [telemetryFilter, setTelemetryFilter] = useState<TelemetryFilter>("priority");
  const [telemetrySearch, setTelemetrySearch] = useState("");

  const telecommandOptions = (component?.value as SelectOption[] | undefined) ?? [];
  const isTelecommandsLoading = isComponentsLoading;
  const canOpenTelecommand = !!component;
  const canOpenTelemetry = !!selectedCommand;

  const getTelemetryOptionValue = (option: SelectOption): string => {
    if (option.value === SELECT_ALL_VALUE) return SELECT_ALL_VALUE;
    const telemetry = option.value as {
      commandId?: number;
      saId?: number;
      daId?: number;
      subsystem?: string;
      commandName?: string;
    };

    return [
      telemetry.commandId ?? "na",
      telemetry.saId ?? "na",
      telemetry.daId ?? "na",
      telemetry.subsystem ?? "na",
      telemetry.commandName ?? option.label,
    ].join("::");
  };

  const getTelecommandOptionValue = (option: SelectOption): string => {
    const commandValue = option.value as Record<string, unknown> | undefined;
    const sequenceData = commandValue?.sequenceData;
    const seqKey = sequenceData ? JSON.stringify(sequenceData) : "no-sequence";
    return `${option.label}::${seqKey}`;
  };

  const getTelemetryMatchesFilter = (option: SelectOption, filter: TelemetryFilter): boolean => {
    if (filter === "all") return true;
    if (filter === "priority") return !option.isUnassociated;
    return !!option.isUnassociated;
  };

  const normalizeTelemetrySearch = (value: unknown): string => String(value ?? "").toLowerCase().replace(/[^a-z0-9]+/g, "");

  const getTelemetrySearchText = (option: SelectOption): string => {
    const telemetry = option.value as {
      commandId?: number | string;
      saId?: number | string;
      daId?: number | string;
      subsystem?: string;
      commandName?: string;
    } | undefined;

    return [
      option.label,
      telemetry?.commandName,
      telemetry?.subsystem,
      telemetry?.commandId,
      telemetry?.saId,
      telemetry?.daId,
    ].filter((value) => value !== undefined && value !== null).join(" ");
  };

  const getTelemetryMatchesSearch = (option: SelectOption, searchText: string): boolean => {
    const terms = searchText.trim().split(/\s+/).filter(Boolean);
    if (terms.length === 0) return true;

    const haystack = getTelemetrySearchText(option).toLowerCase();
    const compactHaystack = normalizeTelemetrySearch(haystack);

    return terms.every((term) => {
      const lowerTerm = term.toLowerCase();
      const compactTerm = normalizeTelemetrySearch(term);
      return haystack.includes(lowerTerm) || (compactTerm !== "" && compactHaystack.includes(compactTerm));
    });
  };

  const getTelemetrySearchRank = (option: SelectOption, searchText: string): number => {
    const telemetry = option.value as {
      commandName?: string;
      subsystem?: string;
    } | undefined;
    const query = searchText.trim().toLowerCase();
    const compactQuery = normalizeTelemetrySearch(query);
    const label = option.label.toLowerCase();
    const compactLabel = normalizeTelemetrySearch(option.label);
    const commandName = String(telemetry?.commandName ?? "").toLowerCase();
    const subsystem = String(telemetry?.subsystem ?? "").toLowerCase();
    const groupRank = option.isRecommended ? 0 : option.isUnassociated ? 3 : option.isSecondary ? 2 : 1;

    if (query !== "" && (label === query || compactLabel === compactQuery)) return groupRank * 20;
    if (query !== "" && (label.startsWith(query) || compactLabel.startsWith(compactQuery))) return groupRank * 20 + 1;
    if (query !== "" && (label.includes(query) || compactLabel.includes(compactQuery))) return groupRank * 20 + 2;
    if (query !== "" && commandName.includes(query)) return groupRank * 20 + 3;
    if (query !== "" && subsystem.includes(query)) return groupRank * 20 + 4;
    return groupRank * 20 + 5;
  };

  const telemetryFilterCounts = useMemo(() => ({
    priority: telemetryOptions.filter((option) => getTelemetryMatchesFilter(option, "priority")).length,
    unassociated: telemetryOptions.filter((option) => option.isUnassociated).length,
    all: telemetryOptions.length,
  }), [telemetryOptions]);

  const visibleTelemetryOptions = useMemo(
    () => telemetryOptions.filter((option) => getTelemetryMatchesFilter(option, telemetryFilter)),
    [telemetryOptions, telemetryFilter]
  );

  const isTelemetrySearchActive = telemetrySearch.trim().length > 0;
  const telemetrySearchTerms = useMemo(() => getTelemetrySearchTerms(telemetrySearch), [telemetrySearch]);

  const selectableTelemetryOptions = useMemo(
    () => {
      if (!isTelemetrySearchActive) return visibleTelemetryOptions;
      return telemetryOptions
        .map((option, index) => ({
          option,
          index,
          rank: getTelemetrySearchRank(option, telemetrySearch),
        }))
        .filter(({ option }) => getTelemetryMatchesSearch(option, telemetrySearch))
        .sort((a, b) => a.rank - b.rank || a.index - b.index)
        .map(({ option }) => option);
    },
    [isTelemetrySearchActive, telemetryOptions, telemetrySearch, visibleTelemetryOptions]
  );

  const telemetrySearchSummary = useMemo<TelemetrySearchSummary | null>(() => {
    if (!isTelemetrySearchActive) return null;
    return selectableTelemetryOptions.reduce<TelemetrySearchSummary>((summary, option) => ({
      total: summary.total + 1,
      relevant: summary.relevant + (option.isUnassociated ? 0 : 1),
      unassociated: summary.unassociated + (option.isUnassociated ? 1 : 0),
    }), { total: 0, relevant: 0, unassociated: 0 });
  }, [isTelemetrySearchActive, selectableTelemetryOptions]);

  const selectedTelemetryKeys = useMemo(
    () => new Set(selectedTelemetries.map((option) => getTelemetryOptionValue(option))),
    [selectedTelemetries]
  );

  const isAllTelemetriesSelected = selectableTelemetryOptions.length > 0
    && selectableTelemetryOptions.every((option) => selectedTelemetryKeys.has(getTelemetryOptionValue(option)));

  const pinnedSelectedOptions = useMemo(() => selectedTelemetries.map((option, idx) => ({
    ...option,
    isSelectedPinned: true,
    sectionLabel: idx === 0 ? "Selected TMs" : undefined,
    showSectionDivider: false,
    highlightTerms: isTelemetrySearchActive && getTelemetryMatchesSearch(option, telemetrySearch) ? telemetrySearchTerms : undefined,
  })), [isTelemetrySearchActive, selectedTelemetries, telemetrySearch, telemetrySearchTerms]);

  const unselectedVisibleOptions = useMemo(() => {
    let lastGroup: TelemetryOptionGroup | null = null;

    return selectableTelemetryOptions
      .filter((option) => !selectedTelemetryKeys.has(getTelemetryOptionValue(option)))
      .map((option) => {
        const group = getTelemetryOptionGroup(option);
        const startsGroup = group !== lastGroup;
        lastGroup = group;

        return {
          ...option,
          sectionLabel: startsGroup ? getTelemetryOptionSectionLabel(option) : undefined,
          showSectionDivider: startsGroup && option.showSectionDivider,
          highlightTerms: isTelemetrySearchActive ? telemetrySearchTerms : undefined,
        };
      });
  }, [isTelemetrySearchActive, selectableTelemetryOptions, selectedTelemetryKeys, telemetrySearchTerms]);

  const groupedTelemetryOptions = useMemo(
    () => [...pinnedSelectedOptions, ...unselectedVisibleOptions],
    [pinnedSelectedOptions, unselectedVisibleOptions]
  );

  const showTelemetrySelectAll = selectableTelemetryOptions.length > 1
    || (pinnedSelectedOptions.length > 0 && selectableTelemetryOptions.length > 0);

  const telemetrySelectOptions: SelectOption[] = showTelemetrySelectAll
    ? [{ label: "Select All", value: SELECT_ALL_VALUE }, ...groupedTelemetryOptions]
    : groupedTelemetryOptions;

  const handleTelemetrySearchClear = useCallback(() => {
    setTelemetrySearch("");
  }, []);

  const handleTelemetryInternalChange = (vals: MultiValue<SelectOption>) => {
    const values = (vals as SelectOption[]).filter((v) => v.value !== SELECT_ALL_VALUE);
    const hadSelectAll = (vals as SelectOption[]).some((v) => v.value === SELECT_ALL_VALUE);
    let nextSelections: SelectOption[];
    if (hadSelectAll) {
      const selectableKeys = new Set(selectableTelemetryOptions.map((option) => getTelemetryOptionValue(option)));
      const hiddenSelections = selectedTelemetries.filter((option) => !selectableKeys.has(getTelemetryOptionValue(option)));
      nextSelections = isAllTelemetriesSelected
        ? hiddenSelections
        : [
            ...hiddenSelections,
            ...selectableTelemetryOptions,
          ];
    } else {
      nextSelections = values;
    }

    onTelemetriesChange(nextSelections);
  };

  const handleTelemetryInputChange = (value: string, actionMeta: { action?: string }): string => {
    if (actionMeta?.action === "input-change") {
      setTelemetrySearch(value);
      return value;
    }
    if (actionMeta?.action === "menu-close") {
      setTelemetrySearch("");
      return "";
    }
    return value;
  };

  const TelemetrySelectOption = useCallback((props: OptionProps<SelectOption, true, GroupBase<SelectOption>>) => {
    if (props.data.value === SELECT_ALL_VALUE) {
      return (
        <div ref={props.innerRef} {...props.innerProps} className="tcd-tm-option tcd-tm-option--select-all">
          <span className={`tcd-tm-option__cb${isAllTelemetriesSelected ? " checked" : ""}`}>
            {isAllTelemetriesSelected && <CheckMiniIcon />}
          </span>
          <span className="tcd-tm-option__label" style={{ fontStyle: "italic", opacity: 0.75 }}>
            {isTelemetrySearchActive ? `Select matching (${selectableTelemetryOptions.length})` : "Select All"}
          </span>
        </div>
      );
    }
    return <TelemetryOption {...props} />;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAllTelemetriesSelected, isTelemetrySearchActive, selectableTelemetryOptions.length]);

  const TelemetryMenuList = useCallback((props: MenuListProps<SelectOption, true, GroupBase<SelectOption>>) => (
    <selectComponents.MenuList {...props}>
      <div className="tcd-tm-option-filter" onMouseDown={(event) => event.stopPropagation()}>
        <div className="tcd-tm-option-filter__segmented">
          {TELEMETRY_FILTERS.map((filter) => {
            const count = telemetryFilterCounts[filter.key];
            const active = telemetryFilter === filter.key;
            const FilterIcon = filter.Icon;
            return (
              <button
                key={filter.key}
                type="button"
                className={`tcd-tm-option-filter__tab${active ? " active" : ""}`}
                disabled={count === 0}
                aria-pressed={active}
                aria-label={`${filter.label} (${count})`}
                onMouseDown={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  setTelemetrySearch("");
                  setTelemetryFilter(filter.key);
                }}
              >
                <span className="tcd-tm-option-filter__icon">
                  <FilterIcon />
                </span>
                <TelemetryFilterLabel label={filter.label} active={active} />
                <span className="tcd-tm-option-filter__count">{formatTelemetryFilterCount(count)}</span>
              </button>
            );
          })}
        </div>
        {isTelemetrySearchActive && telemetrySearchSummary && (
          <div className="tcd-tm-search-summary">
            <div className="tcd-tm-search-summary__text">
              <span className="tcd-tm-search-summary__total">
                {telemetrySearchSummary.total === 0
                  ? `No matches for "${telemetrySearch.trim()}"`
                  : `${telemetrySearchSummary.total} match${telemetrySearchSummary.total === 1 ? "" : "es"}`}
              </span>
              {telemetrySearchSummary.total > 0 && (
                <span className="tcd-tm-search-summary__groups">
                  {telemetrySearchSummary.relevant > 0 && <span>Relevant {telemetrySearchSummary.relevant}</span>}
                  {telemetrySearchSummary.unassociated > 0 && <span>Unassociated {telemetrySearchSummary.unassociated}</span>}
                </span>
              )}
            </div>
            <button
              type="button"
              className="tcd-tm-search-summary__clear"
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
                handleTelemetrySearchClear();
              }}
            >
              Clear
            </button>
          </div>
        )}
      </div>
      {isTelemetrySearchActive && telemetrySearchSummary?.total === 0 && groupedTelemetryOptions.length === 0 ? (
        <div className="tcd-tm-search-empty">
          <span>Try another telemetry name, subsystem, or ID.</span>
        </div>
      ) : props.children}
    </selectComponents.MenuList>
  ), [groupedTelemetryOptions.length, handleTelemetrySearchClear, isTelemetrySearchActive, telemetryFilter, telemetryFilterCounts, telemetrySearch, telemetrySearchSummary]);

  const delayMs = parseInt(delay) || 0;
  const timeoutMs = parseInt(timeout) || 0;
  const timeoutInvalid = delayMs > 0 && timeoutMs >= delayMs;

  useEffect(() => {
    if (!timeoutInvalid) {
      setTimeoutBubblePos(null);
      return;
    }

    const updatePosition = (): void => {
      const el = timeoutInputWrapRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setTimeoutBubblePos({
        top: rect.top + rect.height / 2,
        left: rect.right + 10,
      });
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);

    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [timeoutInvalid]);

  useEffect(() => {
    setTelemetryFilter("priority");
    setTelemetrySearch("");
  }, [selectedCommand]);

  useEffect(() => {
    if (!forceCloseTelemetryMenu && canOpenTelemetry) return;
    setTelemetrySearch("");
  }, [canOpenTelemetry, forceCloseTelemetryMenu]);

  useEffect(() => {
    if (telemetryFilterCounts.all === 0 || telemetryFilterCounts[telemetryFilter] > 0) return;
    const fallback = TELEMETRY_FILTERS.find((filter) => telemetryFilterCounts[filter.key] > 0);
    if (fallback) setTelemetryFilter(fallback.key);
  }, [telemetryFilter, telemetryFilterCounts]);

  return (
    <div className="tcd-left-panel">
      <div className="tcd-panel-header">
        <span>Add Telecommands</span>
      </div>
      <div className="tcd-panel-body">
        <div className="tcd-dropdowns">
          {/* SubSystem */}
          <div className={`tcd-dropdown-row${isReadOnly ? " disabled" : ""}`}>
            <span className="tcd-dropdown-row__label">Subsystem*</span>
            <CustomSelect
              isDisabled={isReadOnly || isComponentsLoading}
              isLoading={isComponentsLoading}
              mutedLoadingState
              isSearchable={true}
              placeholder={isComponentsLoading ? "Loading..." : "Select"}
              options={busComponents}
              value={component}
              onChange={onComponentChange}
              controllHeight="28px"
            />
          </div>

          {/* Telecommand */}
          <div className={`tcd-dropdown-row${isReadOnly || (!isTelecommandsLoading && !canOpenTelecommand) ? " disabled" : ""}`}>
            <span className="tcd-dropdown-row__label">Telecommand*</span>
            <CustomToolTip title={selectedCommand?.label ?? ""} placement="left" maxWidth="220px" wordBreak="normal" className="w-100">
              <CustomSelect
                isDisabled={isReadOnly || isTelecommandsLoading || !canOpenTelecommand}
                isLoading={isTelecommandsLoading}
                mutedLoadingState
                isSearchable={true}
                placeholder={isTelecommandsLoading ? "Loading..." : "Select Telecommand"}
                options={telecommandOptions}
                value={selectedCommand}
                onChange={onTelecommandChange}
                getOptionValue={getTelecommandOptionValue}
                controllHeight="28px"
                filterOption={(option: any, rawInput: string) => {
                  const input = rawInput.toLowerCase().trim();
                  if (!input) return true;
                  return option.label.toLowerCase().includes(input);
                }}
              />
            </CustomToolTip>
          </div>

          {/* Telemetry — auto-selected and read-only when only 1 option */}
          <div className={`tcd-dropdown-row${isReadOnly || (!isTelemetriesLoading && !canOpenTelemetry) ? " disabled" : ""}`}>
            <span className="tcd-dropdown-row__label">Telemetry </span>
            {!isTelemetriesLoading && selectedCommand && telemetryOptions.length === 1 ? (
              <CustomToolTip title={telemetryOptions[0].label} placement="right" maxWidth="220px" wordBreak="normal" className="w-100">
                <span className={`tcd-tm-auto-label${telemetryOptions[0].isRecommended ? " recommended" : ""}`}>
                  <span className="tcd-tm-auto-label__text">{telemetryOptions[0].label}</span>
                </span>
              </CustomToolTip>
            ) : (
              <CustomSelect
                isMulti
                isDisabled={isReadOnly || isTelemetriesLoading || !canOpenTelemetry}
                isLoading={isTelemetriesLoading}
                mutedLoadingState
                isSearchable={true}
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                placeholder={isTelemetriesLoading ? "Loading..." : "Select Telemetries"}
                options={telemetrySelectOptions}
                value={selectedTelemetries}
                onChange={(vals) => handleTelemetryInternalChange(vals)}
                inputValue={telemetrySearch}
                onInputChange={handleTelemetryInputChange}
                forceCloseMenu={forceCloseTelemetryMenu || isTelemetriesLoading || !canOpenTelemetry || isReadOnly}
                filterOption={() => true}
                getOptionValue={getTelemetryOptionValue}
                controllHeight="28px"
                menuHeight="280px"
                overrideComponents={{ Option: TelemetrySelectOption, ValueContainer: TelemetryValueContainer, MenuList: TelemetryMenuList }}
              />
            )}
          </div>
        </div>

        <div className="tcd-divider" />

        {/* Scheduling */}
        <div className={`tcd-scheduling`}>
          <div className="tcd-section-label">Scheduling Specifications</div>
          <div className="tcd-spec-rows">
            <div className="tcd-spec-row">
              <div className="tcd-spec-label">
                <span>Delay*</span>
                <CustomToolTip title="Delay is the time between end time of previous command and start time of this command." placement="top-start" maxWidth="220px" wordBreak="normal">
                  <button className="tcd-info-btn" type="button" tabIndex={-1}><InfoIcon /></button>
                </CustomToolTip>
              </div>
              <div className="tcd-spec-input">
                <input className="tcd-spec-number" type="number" min={0} value={delay} onChange={(e: React.ChangeEvent<HTMLInputElement>) => onDelayChange(e.target.value)} placeholder="0" disabled={isReadOnly} data-testid="tc-detail-delay-input" />
                <span className="tcd-spec-unit">ms</span>
              </div>
            </div>
            <div className="tcd-spec-row tcd-spec-row--timeout">
              <div className="tcd-spec-label">
                <span>Timeout</span>
                <CustomToolTip title="Time to wait for TM response before marking the TC as Failed. Must be less than Delay." placement="bottom-start" maxWidth="220px" wordBreak="normal">
                  <button className="tcd-info-btn" type="button" tabIndex={-1}><InfoIcon /></button>
                </CustomToolTip>
              </div>
              <div className="tcd-spec-input" ref={timeoutInputWrapRef}>
                <input
                  className={`tcd-spec-number${timeoutInvalid ? " tcd-spec-number--invalid" : ""}`}
                  type="number"
                  min={0}
                  value={timeout}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => onTimeoutChange(e.target.value)}
                  placeholder="0"
                  disabled={isReadOnly}
                  data-testid="tc-detail-timeout-input"
                />
                <span className="tcd-spec-unit">ms</span>
              </div>
            </div>
            <div className={`tcd-spec-row tcd-spec-row--critical${!isCritical ? " tcd-spec-row--off" : ""}`}>
              <div className="tcd-spec-label">
                <span>Mark as Critical</span>
                <CustomToolTip
                  title="Marks this telecommand as critical to control further execution in case of failure."
                  placement="bottom-start"
                  maxWidth="220px"
                  wordBreak="normal"
                >
                  <button className="tcd-info-btn" type="button" tabIndex={-1}><InfoIcon /></button>
                </CustomToolTip>
              </div>
              <label className="tcd-switch">
                <input
                  type="checkbox"
                  checked={isCritical}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => onIsCriticalChange(e.target.checked)}
                  disabled={isReadOnly}
                  data-testid="tc-detail-critical-switch"
                />
                <span className="tcd-slider round"></span>
              </label>
            </div>
          </div>
        </div>
      </div>

      {timeoutInvalid && timeoutBubblePos && createPortal(
        <div
          className="tcd-timeout-warning-bubble tcd-timeout-warning-bubble--floating"
          role="alert"
          aria-live="polite"
          style={{ top: `${timeoutBubblePos.top}px`, left: `${timeoutBubblePos.left}px` }}
        >
          <span>Timeout must be lower than the Delay value.</span>
        </div>,
        document.body
      )}
    </div>
  );
};

export default LeftPanel;
